import { EditOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Flex, Input, message, Modal, Switch, Typography } from "antd";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useBlocker } from "react-router-dom";
import ReactFlow, { Background, BackgroundVariant, ReactFlowProvider, type Edge, type Node, type NodeChange } from "reactflow";
import "reactflow/dist/style.css";
import HButton from "../../components/custom/input/button/Button";
import { AppContext } from "../../context/AppContext";
import useHttp from "../../hooks/useHttp";
import { CommonLoadingV2, CommonModal } from "../utils/CommonVessels";
import { ActionConfigPanel } from "./components/ActionConfigPanel";
import { ConditionConfigPanel } from "./components/ConditionConfigPanel";
import { WorkflowCustomControls } from "./components/WorkflowCustomControls";
import { ConnectorEdge } from "./edges/ConnectorEdge";
import type { WorkflowEnumItem } from "./hooks/useWorkflowEnums";
import { useWorkflowEnums } from "./hooks/useWorkflowEnums";
import { ActionNode } from "./nodes/ActionNode";
import { AddStepNode } from "./nodes/AddStepNode";
import { ConditionNode } from "./nodes/ConditionNode";
import { EndNode } from "./nodes/EndNode";
import { Trigger, TriggerNode, type TriggerType } from "./nodes/TriggerNode";
import type { ServerWorkflow } from "./parser/types";
import type { StepDefinition } from "./parser/workflowParser";
import { useWorkflowStore } from "./store";
import { selectHasChanged, selectSelectedStep } from "./store/selectors";
import "./triggerRules.css";
import type { WorkflowRecord } from "./TriggerRulesList";

const { Text } = Typography;

const TRIGGER_TYPE_TO_CODE: Record<TriggerType, string> = {
    [Trigger.Message.Inbound]: Trigger.Message.Inbound,
    [Trigger.Message.Outbound]: Trigger.Message.Outbound,
};

function filterBySupportedTriggers<T extends WorkflowEnumItem>(
    items: T[],
    triggerLabel: string
): T[] {
    return items.filter((item) => {
        const supported = item.meta?.supportedTriggers;
        if (!supported || supported.length === 0) return true;
        return supported.includes(triggerLabel);
    });
}

const NODE_WIDTH = 350;
const ADD_STEP_SIZE = 32;
const ADD_STEP_GAP = 80;
const DEFAULT_NODE_HEIGHT = 100;

function isParentStepConfigured(step: StepDefinition): boolean {
    if (step.type === "trigger") return true;

    if (step.type === "condition") {
        const data = (step.data ?? {}) as { isPlaceholder?: boolean; conditionCode?: string };
        return !data.isPlaceholder && Boolean(data.conditionCode);
    }

    if (step.type === "action") {
        const data = (step.data ?? {}) as { isPlaceholder?: boolean; actionCode?: string; actionType?: string };
        return !data.isPlaceholder && Boolean(data.actionCode || data.actionType);
    }

    return true;
}

function buildFlowFromSteps(
    steps: StepDefinition[],
    selectedNodeId: string | null,
    measuredHeights: Record<string, number>,
    triggerType: TriggerType
): { nodes: Node[]; edges: Edge[] } {
    const isFlowEditable = triggerType !== Trigger.Message.Outbound;
    const hasAnyPlaceholder = steps.some((s) => s.type !== "trigger" && !isParentStepConfigured(s));
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const nodeX = 0;
    const addStepX = (NODE_WIDTH - ADD_STEP_SIZE) / 2;

    let y = 0;

    for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        const nodeHeight = measuredHeights[step.id] ?? DEFAULT_NODE_HEIGHT;

        nodes.push({
            id: step.id,
            type: step.type,
            position: { x: nodeX, y },
            data: step.data,
            draggable: false,
            selected: selectedNodeId === step.id,
        });

        const addStepId = `addstep-after-${step.id}`;
        const addStepY = y + nodeHeight + (ADD_STEP_GAP - ADD_STEP_SIZE) / 2;

        nodes.push({
            id: addStepId,
            type: "addStep",
            position: { x: addStepX, y: addStepY },
            data: {
                insertIndex: i + 1,
                parentStepId: step.id,
                isEnabled: !hasAnyPlaceholder && isParentStepConfigured(step) && isFlowEditable,
            },
            draggable: false,
        });

        edges.push({
            id: `e-${step.id}-${addStepId}`,
            source: step.id,
            target: addStepId,
        });

        if (i + 1 < steps.length) {
            edges.push({
                id: `e-${addStepId}-${steps[i + 1].id}`,
                source: addStepId,
                target: steps[i + 1].id,
            });
        }

        y += nodeHeight + ADD_STEP_GAP;
    }

    if (steps.length > 0) {
        const lastStep = steps[steps.length - 1];
        const lastStepHeight = measuredHeights[lastStep.id] ?? DEFAULT_NODE_HEIGHT;
        const lastStepY = y - (lastStepHeight + ADD_STEP_GAP);
        const lastAddStepId = `addstep-after-${lastStep.id}`;
        const lastAddStepY = lastStepY + lastStepHeight + (ADD_STEP_GAP - ADD_STEP_SIZE) / 2;
        const endNodeId = "workflow-end-node";

        nodes.push({
            id: endNodeId,
            type: "end",
            position: { x: addStepX - 6, y: lastAddStepY + ADD_STEP_SIZE + 10 },
            data: {},
            draggable: false,
            selectable: false,
        });
        edges.push({
            id: `e-${lastAddStepId}-${endNodeId}`,
            source: lastAddStepId,
            target: endNodeId,
        });
    }

    return { nodes, edges };
}

export interface TriggerRulesEditorProps {
    workflow: WorkflowRecord | null;
    serverWorkflow?: ServerWorkflow | null;
    onBack: () => void;
    onWorkflowSaved?: (workflowId: string) => void;
    triggerType?: TriggerType;
}

const TriggerRulesEditorContent: React.FC<TriggerRulesEditorProps> = ({
    workflow,
    serverWorkflow,
    onBack,
    onWorkflowSaved,
    triggerType = Trigger.Message.Inbound,
}) => {
    const [appProperties] = useContext(AppContext);
    const { fetchData } = useHttp();
    const { conditionTypes, actionTypes, scheduleOptions } = useWorkflowEnums();

    const storeTriggerType = useWorkflowStore((s) => s.triggerType);
    const triggerCode = TRIGGER_TYPE_TO_CODE[storeTriggerType];
    const filteredConditionTypes = useMemo(
        () => filterBySupportedTriggers(conditionTypes, triggerCode),
        [conditionTypes, triggerCode]
    );
    const filteredActionTypes = useMemo(
        () => filterBySupportedTriggers(actionTypes, triggerCode),
        [actionTypes, triggerCode]
    );

    const nodeTypes = useMemo(
        () => ({
            trigger: TriggerNode,
            condition: ConditionNode,
            action: ActionNode,
            addStep: AddStepNode,
            end: EndNode,
        }),
        []
    );
    const edgeTypes = useMemo(() => ({ default: ConnectorEdge }), []);

    const steps = useWorkflowStore((s) => s.steps);
    const selectedNodeId = useWorkflowStore((s) => s.selectedNodeId);
    const workflowName = useWorkflowStore((s) => s.workflowName);
    const measuredHeights = useWorkflowStore((s) => s.measuredHeights);
    const selectNode = useWorkflowStore((s) => s.selectNode);
    const setWorkflowName = useWorkflowStore((s) => s.setWorkflowName);
    const updateMeasuredHeights = useWorkflowStore((s) => s.updateMeasuredHeights);
    const deleteStep = useWorkflowStore((s) => s.deleteStep);
    const addStep = useWorkflowStore((s) => s.addStep);
    const incrementCloseSignal = useWorkflowStore((s) => s.incrementCloseSignal);

    const selectedStep = useWorkflowStore(selectSelectedStep);
    const hasChanged = useWorkflowStore(selectHasChanged);
    const serverStatus = useWorkflowStore((s) => s.serverStatus);
    const workflowId = useWorkflowStore((s) => s.workflowId);
    const [saving, setSaving] = useState(false);
    const [toggleStatusLoading, setToggleStatusLoading] = useState(false);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [showDeleteStepConfirm, setShowDeleteStepConfirm] = useState(false);
    const [nodeIdToDelete, setNodeIdToDelete] = useState<string | null>(null);

    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        const currentlyHasChanged = selectHasChanged(useWorkflowStore.getState());
        return (
            currentlyHasChanged &&
            (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search)
        );
    });

    useEffect(() => {
        if (serverWorkflow?.graph) {
            useWorkflowStore.getState().loadFromServer(serverWorkflow);
        } else {
            useWorkflowStore.getState().initNewWorkflow(triggerType);
        }
    }, [serverWorkflow, triggerType]);

    useEffect(() => {
        if (workflow?.workflowName) {
            setWorkflowName(workflow.workflowName);
        }
    }, [workflow?.workflowName, setWorkflowName]);

    useEffect(() => {
        return () => {
            useWorkflowStore.getState().clearStore();
        };
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasChanged) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [hasChanged]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const { selectedNodeId: currentSelectedId, triggerType: currentTriggerType, steps: currentSteps } =
                useWorkflowStore.getState();
            if (!currentSelectedId) return;
            if (currentTriggerType === Trigger.Message.Outbound) return;

            const target = e.target as HTMLElement;
            if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) return;

            if (e.key === "Delete" || e.key === "Backspace") {
                e.preventDefault();
                const step = currentSteps.find((s) => s.id === currentSelectedId);
                const isSaved = step && step.type !== "trigger" && !(step.data as { isPlaceholder?: boolean })?.isPlaceholder;
                if (isSaved) {
                    setNodeIdToDelete(currentSelectedId);
                    setShowDeleteStepConfirm(true);
                } else {
                    deleteStep(currentSelectedId);
                }
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [deleteStep]);

    const { nodes, edges } = useMemo(
        () => buildFlowFromSteps(steps, selectedNodeId, measuredHeights, storeTriggerType),
        [steps, selectedNodeId, measuredHeights, storeTriggerType],
    );

    const handleNodesChange = useCallback(
        (changes: NodeChange[]) => {
            const heightChanges: Array<{ id: string; height: number }> = [];
            for (const change of changes) {
                if (change.type === "dimensions" && change.dimensions?.height) {
                    heightChanges.push({ id: change.id, height: change.dimensions.height });
                }
            }
            if (heightChanges.length > 0) {
                requestAnimationFrame(() => updateMeasuredHeights(heightChanges));
            }
        },
        [updateMeasuredHeights],
    );

    const onNodeClick = useCallback(
        (_: React.MouseEvent, node: Node) => {
            if (node.type === "condition" || node.type === "action") {
                selectNode(node.id);
            }
        },
        [selectNode],
    );

    const onPaneClick = useCallback(
        (e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target?.closest?.(".add-step-popover-overlay") || target?.closest?.(".add-step-popover")) {
                return;
            }
            incrementCloseSignal();

            if (selectedNodeId) {
                const step = steps.find((s) => s.id === selectedNodeId);
                if (step && step.type !== "trigger") {
                    const data = (step.data ?? {}) as {
                        isPlaceholder?: boolean;
                        conditionCode?: string;
                        actionCode?: string;
                        actionType?: string;
                    };
                    const isPlaceholder =
                        data.isPlaceholder ??
                        (step.type === "condition" ? !data.conditionCode : !(data.actionCode || data.actionType));
                    if (isPlaceholder) {
                        deleteStep(selectedNodeId);
                    }
                }
            }
            selectNode(null);
        },
        [selectNode, incrementCloseSignal, deleteStep, selectedNodeId, steps]
    );

    const onMoveStart = useCallback(() => {
        incrementCloseSignal();
    }, [incrementCloseSignal]);

    const handleCancel = useCallback(() => {
        if (hasChanged) {
            setShowUnsavedModal(true);
        } else {
            onBack();
        }
    }, [onBack, hasChanged]);

    const handleCloseUnsavedModal = useCallback(() => {
        if (blocker.state === "blocked") {
            blocker.reset();
        }
        setShowUnsavedModal(false);
    }, [blocker]);

    const discardChanges = useWorkflowStore((s) => s.discardChanges);

    const handleDiscardUnsavedChanges = useCallback(() => {
        if (blocker.state === "blocked") {
            blocker.proceed();
        } else {
            discardChanges();
            setShowUnsavedModal(false);
            onBack();
        }
    }, [blocker, onBack, discardChanges]);

    const handleDeleteStepConfirm = useCallback(() => {
        if (nodeIdToDelete) {
            deleteStep(nodeIdToDelete);
            setNodeIdToDelete(null);
            setShowDeleteStepConfirm(false);
        }
    }, [nodeIdToDelete, deleteStep]);

    const handleDeleteStepCancel = useCallback(() => {
        setNodeIdToDelete(null);
        setShowDeleteStepConfirm(false);
    }, []);

    const handleSave = useCallback(async () => {
        setSaving(true);
        try {
            const wasNewWorkflow = !workflow && !workflowId;
            const saveResult = await useWorkflowStore.getState().saveWorkflow({ fetchData, appProperties });

            if (!saveResult.success) {
                if (saveResult.validationErrors.length > 0) {
                    const firstValidationError = saveResult.validationErrors[0];
                    message.warning(firstValidationError.message);
                    if (firstValidationError.stepId) {
                        selectNode(firstValidationError.stepId);
                    }
                } else {
                    message.error("Failed to save workflow. Please try again.");
                }
                return;
            }

            message.success("Workflow saved successfully.");
            if (wasNewWorkflow) {
                const newWorkflowId = useWorkflowStore.getState().workflowId;
                if (newWorkflowId) onWorkflowSaved?.(newWorkflowId);
            }
        } catch {
            message.error("Failed to save workflow.");
        } finally {
            setSaving(false);
        }
    }, [fetchData, appProperties, selectNode, workflow, workflowId, onWorkflowSaved]);

    const isWorkflowEnabled = (serverStatus ?? "").toLowerCase() === "active";

    const handleToggleWorkflowStatus = useCallback(
        async (enabled: boolean) => {
            if (!workflowId || !appProperties || storeTriggerType === Trigger.Message.Outbound) return;

            setToggleStatusLoading(true);
            try {
                const result = await useWorkflowStore
                    .getState()
                    .setWorkflowEnabled({ fetchData, appProperties }, enabled);
                if (result.success) {
                    message.success(
                        enabled ? "Workflow enabled successfully." : "Workflow disabled successfully."
                    );
                } else {
                    message.error(
                        enabled ? "Failed to enable workflow." : "Failed to disable workflow."
                    );
                }
            } catch {
                message.error(
                    enabled ? "Failed to enable workflow." : "Failed to disable workflow."
                );
            } finally {
                setToggleStatusLoading(false);
            }
        },
        [workflowId, appProperties, fetchData, storeTriggerType]
    );

    const handleQuickAddStep = useCallback(
        (type: "condition" | "action") => {
            if (steps.some((s) => s.type !== "trigger" && !isParentStepConfigured(s))) return;
            addStep(steps.length, type);
        },
        [addStep, steps]
    );

    const unsavedModalBody = (
        <>
            <div>
                <div className="ms-2">
                    <h5 className="hs-color-dark-blue">Unsaved changes</h5>
                    <div className="hs-color-dark-blue">Do you want to save or discard changes?</div>
                </div>
            </div>
            <div className="d-flex align-items-center justify-content-end">
                <Button
                    className="mt-2 row-reverse d-inline-block hs-btn-cancel-small w-100 h-45 hs-fs-14"
                    onClick={handleCloseUnsavedModal}
                >
                    Cancel
                </Button>
                <Button
                    className="hs-btn-small ms-3 mt-2 row-reverse d-inline-block w-100 h-45 confirmDeleteBtn"
                    onClick={handleDiscardUnsavedChanges}
                >
                    Discard
                </Button>
            </div>
        </>
    );

    if (!appProperties) return <CommonLoadingV2 />;

    return (
        <>
            <CommonModal
                width={350}
                body={unsavedModalBody}
                open={showUnsavedModal || blocker.state === "blocked"}
                doOnClose={handleCloseUnsavedModal}
                cancelButtonProps={false}
                okButtonProps={false}
                modalCloseIcon={true}
                header={
                    <div style={{ cursor: "default" }} className="actionIconsSpriteForSideBar unsaved-modal-icon" />
                }
            />
            <Modal
                open={showDeleteStepConfirm}
                onCancel={handleDeleteStepCancel}
                centered
                width={400}
                className="campaignOverviewEdit"
                footer={
                    <div className="d-flex align-items-center justify-content-between gap-2 mt-3 m-2">
                        <HButton
                            variant="cancel"
                            buttonClassName="w-100 me-2 h-45 hs-fw-500"
                            onClick={handleDeleteStepCancel}
                        >
                            <span className="hs-fs-14 hs-fw-500 hs-color-text-grey-3">Cancel</span>
                        </HButton>
                        <HButton
                            buttonClassName="w-100 ms-2 h-45 hs-fw-600"
                            type="primary"
                            onClick={handleDeleteStepConfirm}
                        >
                            Delete
                        </HButton>
                    </div>
                }
            >
                <div className="d-flex align-items-start justify-content-between">
                    <div className="pt-1">
                        <ExclamationCircleOutlined className="text-danger fs-4" />
                    </div>
                    <button
                        type="button"
                        className="btn btn-link p-0 border-0 text-decoration-none"
                        onClick={handleDeleteStepCancel}
                        aria-label="Close delete confirmation"
                    >
                        <div className="actionIconsSprite closeIconBig" />
                    </button>
                </div>
                <div className="hs-fs-16 hs-fw-600 hs-color-BlackRussian mb-1 mt-2">Delete this step?</div>
                <div className="pb-3">
                    This step will be removed from the workflow. You can add it back later if needed.
                </div>
            </Modal>
            <Flex className="trigger-rules">
            <div className="d-flex flex-column flex-fill hs-min-h-0">
                <Flex className="py-3 px-3 hs-border-bottom-light" justify="space-between" align="center">
                    <Flex align="center" gap={12}>
                        <Input
                            value={workflowName}
                            onChange={(e) => setWorkflowName(e.target.value)}
                            suffix={<EditOutlined className="trigger-rules-edit-icon" aria-hidden />}
                            aria-label="Workflow name"
                        />
                    </Flex>
                    <Flex gap={12} wrap="wrap" align="center" className="d-flex align-items-center">
                            <Flex align="center" gap={8}>
                                <Text className="hs-fs-12 hs-color-mediumDark">Active</Text>
                                <Switch
                                    className="trigger-rules-status-switch"
                                    checked={isWorkflowEnabled}
                                    onChange={handleToggleWorkflowStatus}
                                    disabled={
                                        toggleStatusLoading ||
                                        !workflowId ||
                                        storeTriggerType === Trigger.Message.Outbound
                                    }
                                    loading={toggleStatusLoading}
                                    aria-label={
                                        storeTriggerType === Trigger.Message.Outbound
                                            ? "Predefined workflow cannot be disabled"
                                            : isWorkflowEnabled
                                              ? "Disable workflow"
                                              : "Enable workflow"
                                    }
                                />
                            </Flex>
                        <div className="vr hs-color-mediumDark" aria-hidden />
                        <HButton type="text" onClick={handleCancel}>
                            Cancel
                        </HButton>
                        <HButton
                            type="primary"
                            onClick={handleSave}
                            loading={saving}
                            disabled={saving || !hasChanged}
                            buttonClassName="hs-min-w-70-px"
                        >
                            Save
                        </HButton>
                    </Flex>
                </Flex>

                <div className="d-flex flex-fill hs-min-h-0" role="main" aria-label="Workflow canvas">
                    <div className="position-relative hs-bg-light-grey-1 d-flex justify-content-center align-items-center flex-fill">
                        <ReactFlow
                            className="hs-bg-light-grey-1"
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={handleNodesChange}
                            onNodeClick={onNodeClick}
                            onPaneClick={onPaneClick}
                            onMoveStart={onMoveStart}
                            nodeTypes={nodeTypes}
                            edgeTypes={edgeTypes}
                            onlyRenderVisibleElements
                            defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                            fitView
                            fitViewOptions={{ padding: 0.2, minZoom: 0.9, maxZoom: 1.5 }}
                            minZoom={0.3}
                            maxZoom={1.5}
                            panOnScroll
                            zoomOnScroll={false}
                            zoomOnPinch
                            nodesDraggable={false}
                            nodesConnectable={false}
                            elementsSelectable
                            proOptions={{ hideAttribution: true }}
                        >
                            <Background variant={BackgroundVariant.Dots} gap={16} size={1.5} color="#d0d0d0" />
                            <WorkflowCustomControls />
                        </ReactFlow>
                    </div>
                </div>
            </div>
            <div className="hs-w-400-px hs-border-left-light">
                {selectedStep?.type === "condition" ? (
                    <ConditionConfigPanel
                        conditionTypes={filteredConditionTypes}
                        savedNumbers={appProperties?.savedNumbers ?? []}
                        scheduleOptions={scheduleOptions}
                    />
                ) : selectedStep?.type === "action" ? (
                    <ActionConfigPanel actionTypes={filteredActionTypes} />
                ) : (
                    <div className="d-flex flex-column h-100">
                        <div className="trigger-rule-selector-panel-header">
                            <Text className="hs-fw-600 hs-fs-16 hs-color-BlackRussian">
                                Trigger
                            </Text>
                        </div>
                        <div className="flex-fill trigger-rule-config-panel-body-empty d-flex align-items-center justify-content-center overflow-hidden py-4 px-3">
                            <div className="trigger-rule-empty-state d-flex flex-column align-items-center gap-2">
                                <div className="automationCardsSprite trigger-rule-empty-state-icon" />
                                <Text className="hs-fs-18 hs-color-BlackRussian hs-fw-600">Trigger is ready</Text>
                                {storeTriggerType === Trigger.Message.Outbound ? (
                                    <Text className="hs-fs-12 hs-color-mediumDark">
                                        This predefined flow cannot be modified.
                                    </Text>
                                ) : (
                                    <>
                                        <Text className="hs-fs-12 hs-color-mediumDark">
                                            Now add your first action step.
                                        </Text>
                                        <button
                                            type="button"
                                            className="text-decoration-underline hs-fs-13 hs-color-violet cursor-pointer border-0 bg-transparent"
                                            onClick={() => handleQuickAddStep("condition")}
                                        >
                                            Add Condition
                                        </button>
                                        <button
                                            type="button"
                                            className="text-decoration-underline hs-fs-13 hs-color-violet cursor-pointer border-0 bg-transparent"
                                            onClick={() => handleQuickAddStep("action")}
                                        >
                                            Add Action
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Flex>
        </>
    );
};

const TriggerRulesEditor: React.FC<TriggerRulesEditorProps> = (props) => (
    <ReactFlowProvider>
        <TriggerRulesEditorContent {...props} />
    </ReactFlowProvider>
);

export default TriggerRulesEditor;
